// Git Commit Tool - Commits staged changes to the Git repository
// Extends WorkspaceTool - has access to appType, fs, and git

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class GitCommitTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'gitCommitTool';
  }
  /**
   * Commit staged changes to the Git repository
   * @param {string} message - Commit message
   * @param {string} [authorName='Revolt User'] - Author name for commit (overrides user data)
   * @param {string} [authorEmail='user@revolt.app'] - Author email for commit (overrides user data)
   * @param {Object} [options] - Optional options
   * @param {Object} [options.user] - User account object (from AuthContext) - used if authorName/authorEmail not provided
   * @param {boolean} [options.useAccountData=true] - Whether to use account data (false = use defaults)
   * @param {boolean} [options.useEmail=true] - Whether to use account email (only applies if useAccountData is true)
   * @param {Object} [options.workspaceManager] - Optional workspace manager instance
   * @returns {Promise<Object>} Result with commit information
   */
  async gitCommit(
    message,
    authorName = "Revolt User",
    authorEmail = "user@revolt.app",
    options = {}
  ) {
    try {
      if (
        !message ||
        typeof message !== "string" ||
        message.trim().length === 0
      ) {
        return this.returnError({}, "Commit message is required");
      }

      // If author info is default and user is provided, get author info from user
      let finalAuthorName = authorName;
      let finalAuthorEmail = authorEmail;
      const workspaceManager = options.workspaceManager || null;

      if (
        authorName === "Revolt User" &&
        authorEmail === "user@revolt.app" &&
        options.user
      ) {
        const authorInfo = this.getAuthorInfo({
          user: options.user,
          useAccountData: options.useAccountData !== false,
          useEmail: options.useEmail !== false,
        });
        finalAuthorName = authorInfo.name;
        finalAuthorEmail = authorInfo.email;
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.gitCommitForBrowser(
          message,
          finalAuthorName,
          finalAuthorEmail,
          workspaceManager
        );
      }

      if (this.appType === "local") {
        return await this.gitCommitForLocal(
          message,
          finalAuthorName,
          finalAuthorEmail,
          workspaceManager
        );
      }

      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for gitCommit`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|gitcommit|error",
          color1: "red",
        },
        "Failed to commit changes",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Get Git author information from user account with configurable options
   * @param {Object} options - Configuration options
   * @param {Object} [options.user] - User account object (from AuthContext)
   * @param {boolean} [options.useAccountData=true] - Whether to use account data (false = use defaults)
   * @param {boolean} [options.useEmail=true] - Whether to use account email (false = empty string, only applies if useAccountData is true)
   * @returns {Object} Author information with name and email
   */
  getAuthorInfo({ user = null, useAccountData = true, useEmail = true } = {}) {
    // If not using account data, return defaults
    if (!useAccountData) {
      return {
        name: "Revolt User",
        email: "user@revolt.app",
      };
    }

    // If no user provided, fall back to defaults
    if (!user) {
      return {
        name: "Revolt User",
        email: "user@revolt.app",
      };
    }

    // Use account data
    const name = user.username || "Revolt User";
    const email = useEmail ? user.email || "user@revolt.app" : "";

    return {
      name,
      email,
    };
  }

  /**
   * Execute git commit for browser apps using isomorphic-git
   */
  async gitCommitForBrowser(
    message,
    authorName,
    authorEmail,
    workspaceManager
  ) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    if (!this.git) {
      return this.returnError({}, "Isomorphic-git not available");
    }

    const dir = "/files";

    // Check if .git exists
    const gitExists = await this.checkGitExists(fs);
    if (!gitExists) {
      return this.returnError({}, "Git repository not initialized");
    }

    // Check if there are staged changes
    const statusResult = await this.git.statusMatrix({
      fs: this.fs,
      dir,
      filter: (f) => {
        return (
          !f.startsWith(".git/") &&
          f !== ".git" &&
          !f.startsWith(".revolt/") &&
          f !== ".revolt"
        );
      },
    });

    // Check if any files are staged
    const hasStagedChanges = statusResult.some(
      ([filepath, headStatus, workdirStatus, stageStatus]) => {
        if (
          filepath.startsWith(".git/") ||
          filepath === ".git" ||
          filepath.startsWith(".revolt/") ||
          filepath === ".revolt"
        ) {
          return false;
        }
        return stageStatus !== 0;
      }
    );

    if (!hasStagedChanges) {
      return this.returnError({}, "No staged changes to commit");
    }

    try {
      // Create commit
      // Set both author and committer to the same values
      // In the future, committer could be different (e.g., when AI makes changes)
      const commitHash = await this.git.commit({
        fs: this.fs,
        dir,
        message: message.trim(),
        author: {
          name: authorName,
          email: authorEmail,
        },
        committer: {
          name: authorName,
          email: authorEmail,
        },
      });

      logger.log(
        {
          tags: "tools|gitcommit|info",
          color1: "lime",
        },
        `Created commit: ${commitHash}`,
        {
          message: message.trim(),
          hash: commitHash,
        }
      );

      // Verify that staged files are cleared after commit
      // After a commit, the index should match HEAD (staged files should be cleared)
      const statusAfterCommit = await this.git.statusMatrix({
        fs: this.fs,
        dir,
        filter: (f) => {
          return (
            !f.startsWith(".git/") &&
            f !== ".git" &&
            !f.startsWith(".revolt/") &&
            f !== ".revolt"
          );
        },
      });

      const stillStaged = statusAfterCommit.filter(
        ([filepath, headStatus, workdirStatus, stageStatus]) => {
          if (
            filepath.startsWith(".git/") ||
            filepath === ".git" ||
            filepath.startsWith(".revolt/") ||
            filepath === ".revolt"
          ) {
            return false;
          }
          // A file is truly staged if: stageStatus !== 0 AND stageStatus !== headStatus
          return stageStatus !== 0 && stageStatus !== headStatus;
        }
      );

      logger.log(
        {
          tags: "tools|gitcommit|info",
          color1: "lime",
        },
        `Status after commit`,
        {
          commitHash,
          stillStagedCount: stillStaged.length,
          stillStagedFiles: stillStaged.map(([fp]) => fp).slice(0, 10),
        }
      );

      return this.returnSuccess(
        {
          commitHash,
          message: message.trim(),
          authorName,
          authorEmail,
        },
        `Commit created successfully: ${commitHash.substring(0, 7)}`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|gitcommit|error",
          color1: "red",
        },
        "Failed to create commit",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Commit via Revolt Desktop IPC (real `git commit`).
   */
  async gitCommitForLocal(
    message,
    authorName,
    authorEmail,
    _workspaceManager
  ) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.gitCommit) {
      return this.returnError(
        {},
        "Local git commit requires Revolt Desktop (workspaceLocalAppAPI.gitCommit)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const res = await api.gitCommit({
      rootPath: root,
      message: message.trim(),
      authorName,
      authorEmail,
    });

    if (!res?.success) {
      if (res?.gitNotInstalled) {
        return this.returnError(
          { gitNotInstalled: true },
          res.error ||
            "Git is not installed. Install Git and make sure it's on your PATH to use source control in local workspaces."
        );
      }
      return this.returnError(
        {},
        res?.error || "Failed to create commit"
      );
    }

    const commitHash = res.commitHash || "";
    logger.log(
      { tags: "tools|gitcommit|local|info", color1: "lime" },
      "Created commit (local)",
      { hash: commitHash, message: message.trim() }
    );

    return this.returnSuccess(
      {
        commitHash,
        message: message.trim(),
        authorName,
        authorEmail,
      },
      commitHash
        ? `Commit created successfully: ${commitHash.substring(0, 7)}`
        : "Commit created successfully"
    );
  }

  /**
   * Check if .git directory exists
   */
  async checkGitExists(fs) {
    try {
      const stats = await fs.stat("/files/.git");
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";
      return isDir;
    } catch (error) {
      return false;
    }
  }
}
